import { MenuItemType } from 'antd/es/menu/interface';
import { ESidebarMenuKey, ESidebarMenuLabel, sidebarMenuIcons } from 'common/models/commonModel';
import { hasAccess, isFranchisorRole, isSuperAdminRole } from 'common/helpers/permission.helper';
import { EPermissionTab, IPermissionMatrix } from 'entities/Permission/Permission.models';
import { EUserRole } from 'entities/User/User.models';

type MenuItemWithChildren = MenuItemType & { children?: MenuItemType[] };

export const splitMenuPath = (path: string = '') => {
  const parts = path.split('/').filter(Boolean);
  const submenuKeys = parts.slice(0, -1);
  const itemKey = parts[parts.length - 1];

  return { submenuKeys, itemKey };
};

export const validateMenuPath = (menuItems: MenuItemWithChildren[], path: string = '') => {
  let items = menuItems;
  const parts = path.split('/').filter(Boolean);

  for (const part of parts) {
    const foundMenuItem = items.find((m) => m.key === part);

    if (!foundMenuItem) {
      return false;
    }

    items = foundMenuItem.children ?? [];
  }

  return true;
};

// First navigable path in the menu tree (submenu/item keys joined) — used as the redirect target
// when the current CIR path is not available to the user (MBC-84 Phase 2: the tree is
// permission-dependent, so the old hardcoded Generate PO fallback may not exist).
export const getFirstMenuPath = (menuItems: MenuItemWithChildren[]): string | undefined => {
  for (const item of menuItems) {
    if (!item.children) {
      return `${item.key}`;
    }
    if (item.children.length) {
      return `${item.key}/${item.children[0].key}`;
    }
  }

  return undefined;
};

const menuItem = (key: ESidebarMenuKey): MenuItemType => ({
  key,
  label: ESidebarMenuLabel[key],
  icon: sidebarMenuIcons[key],
});

// MBC-84 Phase 2: which permission tab unlocks each CIR sidebar module for franchisor users.
const SIDEBAR_KEY_TO_PERMISSION_TAB: Partial<Record<ESidebarMenuKey, EPermissionTab>> = {
  [ESidebarMenuKey.TrackStoreRealTime]: EPermissionTab.CirTrackStore,
  [ESidebarMenuKey.MonitorSales]: EPermissionTab.CirMonitorSales,
  [ESidebarMenuKey.GeneratePO]: EPermissionTab.CirGeneratePO,
  [ESidebarMenuKey.TrackPO]: EPermissionTab.CirTrackPO,
  [ESidebarMenuKey.ViewDeliveryHistory]: EPermissionTab.CirDeliveryHistory,
  [ESidebarMenuKey.StockReconciliation]: EPermissionTab.CirStockCashRecon,
};

// The full CIR tree — the franchisee sidebar shape. Franchisor/superadmin trees are built by
// filtering this same structure by module permissions.
const buildCirTree = (collapsed: boolean, canSeeKey: (key: ESidebarMenuKey) => boolean): MenuItemWithChildren[] => {
  const dashboardChildren = [
    ESidebarMenuKey.TrackStoreRealTime,
    ESidebarMenuKey.ImproveQsc,
    ESidebarMenuKey.AnalyzeChannels,
    ESidebarMenuKey.MonitorSales,
  ]
    .filter(canSeeKey)
    .map(menuItem);
  const purchaseOrderChildren = [
    ESidebarMenuKey.GeneratePO,
    ESidebarMenuKey.TrackPO,
    ESidebarMenuKey.ViewDeliveryHistory,
  ]
    .filter(canSeeKey)
    .map(menuItem);

  const items: MenuItemWithChildren[] = [];

  if (dashboardChildren.length) {
    items.push({
      key: ESidebarMenuKey.Dashboard,
      label: collapsed ? 'DB' : ESidebarMenuLabel[ESidebarMenuKey.Dashboard],
      children: dashboardChildren,
    });
  }

  if (purchaseOrderChildren.length) {
    items.push({
      key: ESidebarMenuKey.PurchaseOrder,
      label: collapsed ? 'PO' : ESidebarMenuLabel[ESidebarMenuKey.PurchaseOrder],
      children: purchaseOrderChildren,
    });
  }

  if (canSeeKey(ESidebarMenuKey.StockReconciliation)) {
    items.push(menuItem(ESidebarMenuKey.StockReconciliation));
  }

  return items;
};

export const getSidebarMenuItems = (
  collapsed: boolean,
  role?: string,
  permissions?: IPermissionMatrix | null
): MenuItemWithChildren[] => {
  // Franchisee: full tree, untouched (their CIR experience does not change — MBC-84 Phase 2).
  if (role === EUserRole.Franchisee) {
    return buildCirTree(collapsed, () => true);
  }

  // Superadmin: every CIR module. Franchisor: only the modules its profile grants — a
  // profile-less franchisor has no CIR permissions and gets an empty tree (no fallback).
  if (isSuperAdminRole(role)) {
    return buildCirTree(collapsed, () => true);
  }

  if (isFranchisorRole(role)) {
    return buildCirTree(collapsed, (key) => {
      const tab = SIDEBAR_KEY_TO_PERMISSION_TAB[key];

      return !!tab && hasAccess(permissions, tab);
    });
  }

  return [];
};
